import React, { useEffect, useState, useContext } from "react";
import { SeedTeam, SeedItem } from "react-brackets";
import KeyCloackService from "../../../services/KeyCloackService";
import { TournamentMatchesContext } from "../../Context/TournamentMatchesContext";
import MatchTeamName from "./MatchTeamName";
import IMatchTournamentSeed from "../../Interfaces/MatchTournamentSeed";
import IMatch from "../../Interfaces/Match";
import UserProfileWithDetailsModal from "../User/UserProfileWithDetailsModal";
import MatchResultService from "../../../services/MatchResultService";
import { EventsContext } from "../../Context/EventsContext";
import MatchService from "../../../services/MatchService";

type Props = {
  matchId: string;
  seed: IMatchTournamentSeed;
  visible: boolean;
  match: IMatch | undefined;
};

function SingleMatch(props: Props) {
  const { matchId, seed, visible, match } = props;
  const { tournamentMatches } = useContext(TournamentMatchesContext);
  const { socket, isConnected } = useContext(EventsContext);

  const [winner, setWinner] = useState<string>();
  const [multipleMatchResult, setMultipleMatchResult] = useState<{
    winner: string | undefined;
    teamHomeScores: number[];
    teamAwayScores: number[];
  }>();

  const multipleMatch = seed?.matchesId?.length > 1;

  const [homeScore, setHomeScore] = useState<number | null>(null);
  const [awayScore, setAwayScore] = useState<number | null>(null);
  const [mainPlayerHome, setMainPlayerHome] = useState<string | null>(null);
  const [mainPlayerAway, setMainPlayerAway] = useState<string | null>(null);
  const [playerHomeWinner, setPlayerHomeWinner] = useState<boolean>(false);
  const [playerAwayWinner, setPlayerAwayWinner] = useState<boolean>(false);
  const [fromWinnerMatch, setFromWinnerMatch] = useState<Array<IMatch>>([]);
  const [fromLoserMatch, setFromLoserMatch] = useState<Array<IMatch>>([]);

  // Socket listener per aggiornamenti live
  useEffect(() => {
    if (isConnected && socket && matchId) {
      socket.onAny((event, ...args) => {
        if (
          event === matchId &&
          args.toString() !== "update" &&
          args.toString() !== "refresh"
        ) {
          const matchElement = args[0];
          setWinner(matchElement.winner);
          setHomeScore(matchElement.homeScore);
          setAwayScore(matchElement.awayScore);
          setPlayerHomeWinner(matchElement.homeScore > matchElement.awayScore);
          setPlayerAwayWinner(matchElement.awayScore > matchElement.homeScore);
        }
      });

      return () => {
        socket.offAny();
      };
    }
  }, [matchId, isConnected, socket]);

  // Popolamento stato da match
  useEffect(() => {
    if (!match) return;

    const scores = match.result?.scores || [];

    const homeScoreValue = scores[0]?.score ?? null;
    const awayScoreValue = scores[1]?.score ?? null;

    const homePlayerId = scores[0]?.playerResults?.[0]?.playerId || null;
    const awayPlayerId = scores[1]?.playerResults?.[0]?.playerId || null;

    setHomeScore(homeScoreValue);
    setAwayScore(awayScoreValue);
    setMainPlayerHome(homePlayerId);
    setMainPlayerAway(awayPlayerId);

    setWinner(match.winner ?? undefined);

    setPlayerHomeWinner(
      homeScoreValue !== null &&
        awayScoreValue !== null &&
        homeScoreValue > awayScoreValue
    );
    setPlayerAwayWinner(
      homeScoreValue !== null &&
        awayScoreValue !== null &&
        awayScoreValue > homeScoreValue
    );

    if (multipleMatch) {
      setMultipleMatchResult(
        MatchResultService.getMultipleMatchWinnerNoSeed([match])
      );
    }

    setFromWinnerMatch(
      getSourceWinnerMatches(tournamentMatches?.tournament, seed.id)
    );
    setFromLoserMatch(
      getSourceLoserMatches(tournamentMatches?.tournament, seed.id)
    );
  }, [match, multipleMatch, seed.id, tournamentMatches?.tournament]);

  function getSourceWinnerMatches(tournament, matchId) {
    return (
      tournament?.matches?.filter((match) => match.nextMatch === matchId) || []
    );
  }

  function getSourceLoserMatches(tournament, matchId) {
    return (
      tournament?.matches?.filter((match) => match.loserMatch === matchId) || []
    );
  }

  function getMatchById(fromMatch) {
    if (!fromMatch?.matchesId?.[0]) return null;
    const matchId = fromMatch.matchesId[0];
    return (
      tournamentMatches?.matches?.find((match) => match.id === matchId) || null
    );
  }

  function getPlaceHolder(index) {
    if (fromWinnerMatch?.length > 0 && fromLoserMatch?.length > 0) {
      if (getMatchById(fromLoserMatch[0])?.status !== 3)
        return index === 0
          ? `Winner match ${fromWinnerMatch[0]?.id ?? ""}`
          : `Loser match ${fromLoserMatch[0]?.id ?? ""}`;
      else
        return index === 0
          ? `Loser match ${fromLoserMatch[0]?.id ?? ""}`
          : `Winner match ${fromWinnerMatch[0]?.id ?? ""}`;
    }

    const winner = fromWinnerMatch?.[index]?.id ?? "";
    const loser = fromLoserMatch?.[index]?.id ?? "";

    if (winner) return `Winner match ${winner}`;
    if (loser) return `Loser match ${loser}`;

    return "";
  }

  return (
    <SeedItem className="skp-brackets-seed-item" prova="pippo">
      {match ? (
        <div className="skp-brackets-seed-item-team">
          <SeedTeam className="skp-brackets-seed-item-team-home">
            {KeyCloackService.isLoggedIn() && (
              <>
                {tournamentMatches?.tournament?.playerType === 0 && (
                  <>
                    <UserProfileWithDetailsModal
                      userId={mainPlayerHome}
                      tournament={tournamentMatches?.tournament}
                      placeholder={getPlaceHolder(0)}
                    />
                    {mainPlayerHome === winner && (
                      <span className="skp-text-xxsmall skp-text-ternary">
                        <img
                          alt="logo-winner"
                          style={{ width: 24, marginRight: 4, marginLeft: -4 }}
                          src="https://img.icons8.com/external-flaticons-flat-flat-icons/344/external-winner-achievements-flaticons-flat-flat-icons-2.png"
                        />
                      </span>
                    )}
                  </>
                )}
                {tournamentMatches?.tournament?.playerType === 1 && (
                  <MatchTeamName
                    teamId={match?.result?.scores[0]?.teamId}
                    tournament={tournamentMatches?.tournament}
                    hideTeamLabel={true}
                  />
                )}
              </>
            )}
            {!KeyCloackService.isLoggedIn() && (
              <div className="user-profile">
                <figure className="avatar">
                  <span className="text-uppercase">
                    <i className="las la-user"></i>
                  </span>
                </figure>
              </div>
            )}
            {!multipleMatch ? (
              <div className={playerHomeWinner ? "skp-text-quaternary" : ""}>
                {homeScore}
              </div>
            ) : (
              <div>
                <span className="player-score">
                  {multipleMatchResult?.teamHomeScores.length}
                </span>
              </div>
            )}
          </SeedTeam>

          <SeedTeam className="skp-brackets-seed-item-team-away">
            {KeyCloackService.isLoggedIn() && (
              <>
                {tournamentMatches?.tournament?.playerType === 0 && (
                  <>
                    <UserProfileWithDetailsModal
                      userId={mainPlayerAway}
                      tournament={tournamentMatches?.tournament}
                      placeholder={getPlaceHolder(1)}
                    />
                    {mainPlayerAway === winner && (
                      <span className="skp-text-xxsmall skp-text-ternary">
                        <img
                          alt="logo-winner"
                          style={{ width: 24, marginRight: 4, marginLeft: -4 }}
                          src="https://img.icons8.com/external-flaticons-flat-flat-icons/344/external-winner-achievements-flaticons-flat-flat-icons-2.png"
                        />
                      </span>
                    )}
                  </>
                )}
                {tournamentMatches?.tournament?.playerType === 1 && (
                  <MatchTeamName
                    teamId={match?.result?.scores[1]?.teamId}
                    tournament={tournamentMatches?.tournament}
                    hideTeamLabel={true}
                  />
                )}
              </>
            )}
            {!KeyCloackService.isLoggedIn() && (
              <div className="user-profile">
                <figure className="avatar">
                  <span className="text-uppercase">
                    <i className="las la-user"></i>
                  </span>
                </figure>
              </div>
            )}
            {!multipleMatch ? (
              <div className={playerAwayWinner ? "skp-text-quaternary" : ""}>
                {awayScore}
              </div>
            ) : (
              <div>
                <span className="player-score">
                  {multipleMatchResult?.teamAwayScores.length}
                </span>
              </div>
            )}
          </SeedTeam>
        </div>
      ) : (
        <div className="skp-brackets-seed-item-team">
          <SeedTeam className="skp-brackets-seed-item-team-home">
            <UserProfileWithDetailsModal
              userId={undefined}
              tournament={tournamentMatches?.tournament}
            />
          </SeedTeam>
          <SeedTeam className="skp-brackets-seed-item-team-away">
            <UserProfileWithDetailsModal
              userId={undefined}
              tournament={tournamentMatches?.tournament}
            />
          </SeedTeam>
        </div>
      )}
    </SeedItem>
  );
}

export default SingleMatch;
